import { apiClient } from '@/lib/api';
import type { OrgAskRequest, OrgAskResponse } from '@/lib/api';

export interface ToolUseEvent {
  tool?: string;
  status?: string;
  sql?: string;
  message?: string;
}

export interface ToolResultEvent {
  status?: string;
  row_count?: number;
  message?: string;
  result_preview?: unknown;
  tool?: string;
}

export type StreamPhase = 'initial_content' | 'tool_use' | 'tool_result';

export interface StreamProgress {
  phase: StreamPhase;
  headline: string;
  subline?: string;
  timestamp: number;
}

export const PROGRESS_HEADLINES: Record<StreamPhase, string[]> = {
  initial_content: [
    'Hobbes is brewing a fresh pot of insight tea…',
    'Hobbes is adjusting the magnifying glass on your sessions…',
    'Hobbes is stretching whiskers and eyeing the analytics board…',
  ],
  tool_use: [
    'Hobbes is spelunking through the data caverns…',
    'Hobbes is coaxing the query engine into action…',
    'Hobbes is juggling SQL scrolls like a circus pro…',
  ],
  tool_result: [
    'Hobbes is stitching clues into a narrative…',
    'Hobbes is taste-testing the freshest insights…',
    'Hobbes is polishing the takeaways to a mirror shine…',
  ],
};

export const PROGRESS_SUBLINES: Record<StreamPhase, string[]> = {
  initial_content: [
    'Sizing up your sessions before diving in.',
    'Plotting the fastest route through your customer journeys.',
    'Double-checking the compass so no insight gets lost.',
  ],
  tool_use: [
    'Running a few quirky queries to sanity-check the signals.',
    'Letting the SQL sparks fly behind the scenes.',
    'Digging up the juiciest session moments from the vault.',
  ],
  tool_result: [
    'Translating raw numbers into story-ready nuggets.',
    'Tidying up the data crumbs into something snackable.',
    'Making sure the puzzle pieces snap together just right.',
  ],
};

export const GENERIC_STREAM_MESSAGES = new Set([
  'analyzing your data...',
  'processing results...',
  'running analysis...',
  'executing query...',
  'working on it...',
  'now let me analyze the trends with the correct schema:',
]);

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/?$/, '');

export const buildClaudeOptions = () => {
  const base = {
    use_supabase_mcp: true,
    supabase_read_only: true,
    allowed_tools: ['mcp__supabase__query'] as string[],
  };
  const projectRef = import.meta.env.VITE_SUPABASE_PROJECT_REF as string | undefined;
  const supabaseProjectRef = projectRef ?? 'qgsicsriajyqhjmxrbaa';
  return { ...base, supabase_project_ref: supabaseProjectRef };
};

export const normalizeContent = (content?: string) => {
  if (!content) return '';
  const bulletNormalized = content.replace(/(^|\n)\s*•\s+/g, '$1- ');

  const lines = bulletNormalized.split('\n').map((line) => {
    let cleaned = line
      .replace(/\s+([,.;:!?])/g, '$1')
      .replace(/\(\s+/g, '(')
      .replace(/\s+\)/g, ')')
      .replace(/\s+%/g, '%')
      .replace(/\s+\//g, '/')
      .replace(/\/\s+/g, '/');

    cleaned = cleaned.replace(/\s{2,}/g, ' ').trimEnd();
    return cleaned;
  });

  return lines.join('\n').replace(/\n{3,}/g, '\n\n');
};

export const removeEvidenceSection = (content?: string, hasStructuredEvidence?: boolean) => {
  if (!content) return '';
  if (!hasStructuredEvidence) return content;

  const working = content
    .replace(/evidence_json:\s*```[\s\S]*?```/gi, '')
    .replace(/e\s*vid\s*ence\s*_\s*json:\s*```[\s\S]*?```/gi, '')
    .replace(/e\s*vid\s*ence\s*_?\s*json:\s*\[[\s\S]*?\]/gi, '')
    .replace(/^-?\s*e\s*vid\s*ence\s*_?\s*json:\s*\[\s*\]\s*$/gim, '')
    .replace(/^-?\s*e\s*vid\s*ence\s*_?\s*json:\s*$/gim, '')
    .replace(/^\s*\[\s*\]\s*$/gim, '');

  const lines = working.split('\n');
  const filtered: string[] = [];
  let skipping = false;
  let skippingJsonBlock = false;
  let bracketDepth = 0;

  lines.forEach((line) => {
    const trimmed = line.trim();
    const normalized = trimmed.replace(/\s+/g, '').toLowerCase();

    if (!skippingJsonBlock && /^e\s*vid\s*ence\s*_?\s*json\s*:/i.test(trimmed)) {
      const openings = (line.match(/\[/g) ?? []).length;
      const closings = (line.match(/\]/g) ?? []).length;
      bracketDepth = Math.max(0, openings - closings);
      skippingJsonBlock = bracketDepth > 0;
      return;
    }

    if (skippingJsonBlock) {
      bracketDepth += (line.match(/\[/g) ?? []).length;
      bracketDepth -= (line.match(/\]/g) ?? []).length;
      if (bracketDepth <= 0 && /\]/.test(line)) {
        skippingJsonBlock = false;
      }
      return;
    }

    if (!skipping && /^evidence:?$/i.test(trimmed)) {
      skipping = true;
      return;
    }

    if (skipping) {
      if (!trimmed) {
        skipping = false;
      }
      return;
    }

    filtered.push(line);
  });

  return filtered.join('\n');
};

export const prepareResponseContent = (content?: string, hasStructuredEvidence?: boolean) => {
  const withoutEvidence = removeEvidenceSection(content, hasStructuredEvidence);
  return normalizeContent(withoutEvidence).trim();
};

export interface StreamCallbacks {
  onInitialContent?: (text: string) => void;
  onToolUse?: (event: ToolUseEvent) => void;
  onToolResult?: (event: ToolResultEvent) => void;
  onFinalContent?: (text: string) => void;
  onEvidence?: (items: OrgAskResponse['evidence']) => void;
  onError?: (message: string) => void;
}

export const streamAskResponse = async (
  payload: OrgAskRequest,
  callbacks: StreamCallbacks
): Promise<OrgAskResponse> => {
  const token = apiClient.getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const baseUrl = API_BASE_URL || 'http://localhost:8000';
  const sameOrigin =
    typeof window !== 'undefined' && baseUrl.startsWith(window.location.origin);
  const url = `${baseUrl}/api/session-analytics/org/ask-stream`;

  const response = await fetch(url, {
    method: 'POST',
    headers,
    ...(sameOrigin ? { credentials: 'include' } : {}),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Streaming request failed with ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Streaming response body is empty.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const contentChunks: string[] = [];
  const evidenceItems: OrgAskResponse['evidence'] = [];
  let finalContentEmitted = false;
  let doneReceived = false;

  const processEvent = (rawEvent: string) => {
    if (!rawEvent) return;
    const lines = rawEvent.split('\n');
    let eventType = 'message';
    const dataLines: string[] = [];

    lines.forEach((line) => {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5));
      }
    });

    const data = dataLines.join('\n');

    if (import.meta.env.DEV) {
      const preview = data.length > 120 ? `${data.slice(0, 120)}…` : data;
      console.info('[AskHobbes] SSE event', eventType, preview);
    }

    if (eventType === 'initial_content') {
      if (data) {
        callbacks.onInitialContent?.(data);
      }
    } else if (eventType === 'content') {
      if (data) {
        contentChunks.push(data);
        const combined = contentChunks.join('');
        callbacks.onFinalContent?.(combined);
        finalContentEmitted = true;
      }
    } else if (eventType === 'final_content') {
      if (data) {
        contentChunks.length = 0;
        contentChunks.push(data);
        callbacks.onFinalContent?.(data);
        finalContentEmitted = true;
      }
    } else if (eventType === 'tool_use') {
      if (data) {
        try {
          const parsed = JSON.parse(data) as ToolUseEvent;
          callbacks.onToolUse?.(parsed);
        } catch (error) {
          console.error('[AskHobbes] Failed to parse tool_use event', error);
        }
      }
    } else if (eventType === 'tool_result') {
      if (data) {
        try {
          const parsed = JSON.parse(data) as ToolResultEvent;
          callbacks.onToolResult?.(parsed);
        } catch (error) {
          console.error('[AskHobbes] Failed to parse tool_result event', error);
        }
      }
    } else if (eventType === 'error') {
      if (data) {
        try {
          const parsed = JSON.parse(data) as { message?: string };
          if (parsed?.message) {
            callbacks.onError?.(parsed.message);
          }
        } catch (error) {
          console.error('[AskHobbes] Failed to parse error event', error);
        }
      }
    } else if (eventType === 'evidence') {
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (Array.isArray(parsed)) {
            const items = parsed as OrgAskResponse['evidence'];
            if (items.length > 0) {
              evidenceItems.push(...items);
              callbacks.onEvidence?.(items);
            }
          }
        } catch (error) {
          console.error('[AskHobbes] Failed to parse evidence event', error);
        }
      }
    } else if (eventType === 'done') {
      doneReceived = true;
    }
  };

 while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
    buffer = buffer.replace(/\r\n/g, '\n');

    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 2);
      processEvent(rawEvent);
      if (doneReceived) break;
    }

    if (doneReceived || done) {
      if (buffer.trim().length > 0) {
        processEvent(buffer.trim());
      }
      if (import.meta.env.DEV) {
        console.info('[AskHobbes] SSE stream completed');
      }
      break;
    }
  }

  const finalContent = contentChunks.join('');
  if (finalContent && !finalContentEmitted) {
    callbacks.onFinalContent?.(finalContent);
  }

  return {
    content: finalContent || undefined,
    evidence: evidenceItems.length > 0 ? evidenceItems : undefined,
  };
};
